import { useTeamUpdate, type TeamUpdateInput } from "../../api/queries";
import type { Team, UserRole } from "../../api/types";
import { useAbility } from "../../auth/AbilityGate";
import { useAuth } from "../../auth/auth-context";
import { useToast } from "../../ui/toast-context";
import { roleLabels } from "./people";

export interface WorkspaceSettings {
  team: Team | undefined;
  /** `teams.update` is administrator-only (`docs/spec/users-and-teams.md`). */
  canUpdate: boolean;
  /** Sends one patch; a rejection rolls the optimistic write back and toasts. */
  save: (patch: TeamUpdateInput) => void;
  saving: boolean;
}

/**
 * Every workspace settings control writes the same way: patch, optimistic,
 * rollback with an explanation. Screens differ only in which key they send.
 */
export function useWorkspaceSettings(): WorkspaceSettings {
  const { session } = useAuth();
  const team = session?.info.team;
  const canUpdate = useAbility(team?.id, "update");
  const update = useTeamUpdate();
  const toast = useToast();

  return {
    team,
    canUpdate,
    saving: update.isPending,
    save: (patch: TeamUpdateInput) => {
      update.mutate(patch, {
        onError: (error) => {
          toast.show(error.message, "error");
        },
      });
    },
  };
}

export const userRoleOptions: { value: UserRole; label: string }[] = (
  ["admin", "member", "viewer", "guest"] as const
).map((value) => ({ value, label: roleLabels[value] }));

export const audienceOptions = [
  { value: "none" as const, label: "Nobody" },
  { value: "members" as const, label: "Members" },
  { value: "everyone" as const, label: "Everyone, including guests" },
];

export const tocPositionOptions = [
  { value: "left" as const, label: "Left" },
  { value: "right" as const, label: "Right" },
];

/** A host name: labels of letters, digits and hyphens, at least two of them. */
const DOMAIN_PATTERN = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9-]+)+$/u;

export function isValidDomain(value: string): boolean {
  return DOMAIN_PATTERN.test(value.trim().toLowerCase());
}

/** Only the keys whose value actually differs from the workspace record. */
export function changedFields<T extends Record<string, unknown>>(current: T, next: T): Partial<T> {
  const patch: Partial<T> = {};
  for (const key of Object.keys(next) as (keyof T)[]) {
    if (current[key] !== next[key]) patch[key] = next[key];
  }
  return patch;
}
